import { useEffect, useEffectEvent, useLayoutEffect, useRef } from 'react';

import { IconHistory, IconPencilPlus } from '@tabler/icons-react';
import { useAtomValue } from 'jotai';
import { animate, AnimatePresence, motion, useMotionValue } from 'motion/react';
import { useHotkeys } from 'react-hotkeys-hook';

import useAuth from '@/hooks/useAuth';

import { Header } from '@/components/header';

import Conversation from './components/Conversation';
import HistoryDrawer from './components/HistoryDrawer';
import InputPrompt from './components/InputPrompt';
import { useHeaderActions } from './hooks/useHeaderActions';
import {
  SmartSearchChatProvider,
  useSmartSearchChatContext,
} from './SmartSearchChatContext';
import { conversationHistoryAtom, currentConversationIdAtom } from './state';

import { PathwayLogo } from '@/icons';

const SmartSearch: React.FC = () => (
  <SmartSearchChatProvider>
    <SmartSearchLayout />
  </SmartSearchChatProvider>
);

const SmartSearchLayout: React.FC = () => {
  const { data: auth } = useAuth();
  const firstName = auth?.firstName ?? '';

  const chat = useSmartSearchChatContext();
  const currentConversationId = useAtomValue(currentConversationIdAtom);
  const history = useAtomValue(conversationHistoryAtom);

  const storedConversation = currentConversationId
    ? history.find(c => c.id === currentConversationId)
    : undefined;

  const conversationActive = !!chat && chat.messages.length > 0;

  const { handleNewConversation, handleOpenHistoryDrawer } = useHeaderActions();

  const idleRef = useRef<HTMLDivElement>(null);
  const promptRef = useRef<HTMLDivElement>(null);
  const centeredOffsetRef = useRef(0);
  const promptY = useMotionValue(0);

  useHotkeys(
    'meta+shift+o',
    handleNewConversation,
    { enableOnFormTags: true, preventDefault: true },
    [handleNewConversation],
  );

  const layoutEvent = useEffectEvent(() => {
    const container = idleRef.current;
    const prompt = promptRef.current;
    if (!container || !prompt) return;
    const containerH = container.clientHeight;
    const promptH = prompt.clientHeight;
    centeredOffsetRef.current = -(containerH - promptH) / 2;
    if (!conversationActive) {
      promptY.set(centeredOffsetRef.current);
    }
  });

  useLayoutEffect(() => {
    layoutEvent();
  }, []);

  const conversationActiveEvent = useEffectEvent((active: boolean) => {
    animate(promptY, active ? 0 : centeredOffsetRef.current, {
      type: 'spring',
      stiffness: 200,
      damping: 28,
    });
  });

  useEffect(() => {
    conversationActiveEvent(conversationActive);
  }, [conversationActive]);

  const currentTime = new Date().getHours();
  const greeting =
    currentTime < 12
      ? 'Good morning'
      : currentTime < 18
        ? 'Good afternoon'
        : 'Good evening';

  const headerTitle = storedConversation?.title ?? 'Chat';

  return (
    <>
      <Header.Root>
        <Header.Title>{headerTitle}</Header.Title>
        <Header.ActionIconButton
          icon={IconPencilPlus}
          onClick={handleNewConversation}
          tooltip="chat.newConversation"
        />
        <Header.ActionIconButton
          icon={IconHistory}
          onClick={handleOpenHistoryDrawer}
          tooltip="chat.conversationHistory"
        />
      </Header.Root>
      <HistoryDrawer />
      <div
        ref={idleRef}
        className="relative flex h-[calc(100vh-115px)] flex-col"
      >
        <AnimatePresence>
          {!conversationActive && (
            <motion.div
              key="greeting"
              initial={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="pointer-events-none absolute inset-x-0 bottom-1/2 mb-24 flex items-center justify-center gap-4"
            >
              <PathwayLogo className="size-7 shrink-0" />
              <p className="font-serif text-4xl whitespace-nowrap">
                {greeting}, {firstName}!
              </p>
            </motion.div>
          )}
        </AnimatePresence>
        {conversationActive && <Conversation />}
        <motion.div
          ref={promptRef}
          style={{ y: promptY }}
          className="pointer-events-none absolute inset-x-0 bottom-0"
        >
          <div className="pointer-events-auto mx-auto w-full max-w-3xl">
            <InputPrompt />
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default SmartSearch;
